import { useCallback, useEffect, useState } from "react";

import { CredentialsManagerErrorCodes } from "react-native-auth0";

import { BrowserAuthStatus } from "@/hooks/browser/browserAuthStatus";
import useAuth from "@/hooks/useAuth";
import { WebAuth0GetCredentialsArgs } from "@/services/Auth";
import {
  captureAuthErrorMessage,
  handleAuthError,
  isUnrecoverableCredentialsErrorOnAppInit,
  isUnrecoverableCredentialsErrorOnCheckCreds,
} from "@/utils/auth.util";
import { saveReturnToUrl } from "@/utils/browserSessionStorage.util";

const useAppStartupBrowserAuthSession = () => {
  const {
    error,
    getCredentials,
    isAuthenticated,
    isLoading: isAuth0Loading,
  } = useAuth();
  const [hasInitialized, setHasInitialized] = useState(false);
  const [browserAuthStatus, setBrowserAuthStatus] = useState<BrowserAuthStatus>(
    BrowserAuthStatus.LoggedOut,
  );

  // Actively validate Auth0's credentials stored in local storage,
  // to detect if they might actually be expired or invalid.
  const isAuth0StoredSessionValid = useCallback(async (): Promise<boolean> => {
    // getCredentials handles validating the credentials, refreshing the token if needed, etc
    const errorScenario = "Auth0 SDK check creds error";
    const { data: credentials, error: credentialsError } = await getCredentials(
      {
        ...WebAuth0GetCredentialsArgs,
        errorScenario,
      },
    );

    if (!credentialsError) return Boolean(credentials.accessToken);

    if (isUnrecoverableCredentialsErrorOnCheckCreds(credentialsError)) {
      if (
        credentialsError.type === CredentialsManagerErrorCodes.NO_CREDENTIALS
      ) {
        // by default, a NO_CREDENTIALS error is handled silently
        // if this is thrown as an error here, it means things are in a bad/invalid state
        captureAuthErrorMessage({
          error: credentialsError,
          scenario: errorScenario,
        });
      }
      // reporting status=expired to force /v2/logout recovery
      return false;
    }

    // if the error is not unrecoverable, likely a transient issue, so cannot assume it is truly now invalid
    return true;
  }, [getCredentials]);

  // save initial URL (at app startup) to sessionStorage so post-login routing can restore it.
  useEffect(() => {
    const { pathname, search } = globalThis.window.location;
    saveReturnToUrl({ pathname, search });
  }, []);

  useEffect(() => {
    // once useEffect completes once, do not re-run again
    if (hasInitialized) return;
    if (isAuth0Loading) return;

    if (error) {
      handleAuthError({
        error,
        scenario: "Auth0 SDK startup returned an error",
      });
      if (isUnrecoverableCredentialsErrorOnAppInit(error)) {
        // reporting status=expired to force /v2/logout recovery
        setBrowserAuthStatus(BrowserAuthStatus.Expired);
      }

      // recoverable (ie: transient) errors (network, 5xx, etc.) keep the user's session — status stays logged-out
      // so useWebAuthFlow can route to login normally without forcing a /v2/logout round-trip
      setHasInitialized(true);
      return;
    }

    // if no user session, simply proceed to fresh login
    // if there is a user session, it needs more complex validation to be done
    if (!isAuthenticated) {
      setHasInitialized(true);
      return;
    }

    // ensure at most one Auth0 SDK validation running at a time in this hook
    let isCancelled = false;
    void isAuth0StoredSessionValid()
      .then((isValid) => {
        if (isCancelled) return;
        setBrowserAuthStatus(
          isValid ? BrowserAuthStatus.LoggedIn : BrowserAuthStatus.Expired,
        );
      })
      .finally(() => {
        if (isCancelled) return;
        setHasInitialized(true);
      });

    return () => {
      isCancelled = true;
    };
  }, [
    error,
    hasInitialized,
    isAuth0Loading,
    isAuth0StoredSessionValid,
    isAuthenticated,
  ]);

  return { browserAuthStatus, isBrowserAuthLoading: !hasInitialized };
};

export default useAppStartupBrowserAuthSession;
